import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import SideMenu from '@/designs-dev/layout/SideMenu';
import AboutMenu from '@/designs-dev/about/AboutMenu';
import { CHROME_ZOOM } from '@/designs-dev/inspect/chrome';

// Layout route that keeps the designs.dev menu on the left of every prototype.
// Collapsed by default (more room for the prototype); the state lives here, so it
// persists as you move between pages. Collapsed, a small pill opens the menu —
// all of it, wordmark included. The wordmark used to navigate to Overview while
// only the chevron expanded, so clicking the obvious target threw you off the
// page you were working on. Overview is a row inside the menu; getting there can
// cost the extra click.
const MenuLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useKindeAuth();
  const [open, setOpen] = useState(false);
  // On a phone the menu slides over the page rather than sitting beside it, so
  // it shuts once somewhere is picked and a tap outside it shuts it too.
  const theme = useTheme();
  const overlay = useMediaQuery(theme.breakpoints.down('md'));

  // In the About area the sidebar swaps from the prototype nav to the docs nav.
  const inAbout = location.pathname.startsWith('/about');
  const Menu = inAbout ? AboutMenu : SideMenu;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {open ? (
        <>
          <Menu
            currentPath={location.pathname}
            onNavigate={(path) => {
              navigate(path);
              if (overlay) setOpen(false);
            }}
            onSignOut={() => logout()}
            onCollapse={() => setOpen(false)}
          />
          {overlay && (
            <Box
              aria-hidden
              onClick={() => setOpen(false)}
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1240,
                bgcolor: (t) => alpha(t.palette.custom.charcoal, 0.32),
              }}
            />
          )}
        </>
      ) : (
        // One target, one job. The chevron stays as the affordance but is
        // decoration now — a button inside a button is invalid markup, and it
        // was also the thing that made half the pill do something else.
        <Stack
          component='button'
          onClick={() => setOpen(true)}
          aria-label='Open menu'
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1200,
            zoom: CHROME_ZOOM,
            pl: 1.5,
            pr: 1,
            py: 0.5,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'custom.neutral.200',
            borderRadius: `${radius.md}px`,
            cursor: 'pointer',
          }}
        >
          <Typography component='span' variant='h6'>
            Loft
          </Typography>
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>
            <Icon icon='chevrons-right' fontSize={16} />
          </Box>
        </Stack>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default MenuLayout;
